<template>
  <span>
    <button class="hms-btn hms-btn-sm" :class="hasConsent ? 'hms-btn-success' : 'hms-btn-outline-primary'" @click="open" :title="hasConsent ? 'Consent on file' : 'Request consent OTP from patient'">
      <i class="mdi" :class="hasConsent ? 'mdi-check-decagram' : 'mdi-shield-key-outline'"></i>
      {{ hasConsent ? 'Consent ✓' : 'Request consent' }}
    </button>

    <div v-if="dialog" class="hms-modal-backdrop" @click.self="cancel">
      <div class="hms-modal hms-modal-sm">
        <div class="hms-modal-head">
          <h5 class="m-0"><i class="mdi mdi-shield-key"></i> Patient consent — {{ categoryLabel || categoryCode }}</h5>
          <button class="hms-modal-close" @click="cancel"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div v-if="step === 'send'">
            <p class="hms-muted-sm">
              A 6-digit code will be sent to the patient via SMS + email (valid 10 minutes).
              Ask the patient to read it back before sharing this category's data.
            </p>
            <button class="hms-btn hms-btn-primary w-100" :disabled="sending" @click="sendOtp">
              <span v-if="sending"><span class="spinner-border spinner-border-sm me-1"></span> Sending…</span>
              <span v-else><i class="mdi mdi-send"></i> Send OTP to patient</span>
            </button>
          </div>

          <div v-else-if="step === 'verify'">
            <div class="alert" style="background:#e2efda;color:#166534;padding:8px 12px;border-radius:6px;font-size:13px;">
              <i class="mdi mdi-check-circle"></i> OTP sent to {{ channel }} (phone ending {{ phoneMasked }}). Valid 10 min.
            </div>
            <label class="hms-label mt-2">Enter the 6-digit code the patient just received:</label>
            <input v-model="code" type="text" inputmode="numeric" maxlength="6" class="hms-input" placeholder="123456" @keyup.enter="verifyOtp" autofocus />
            <button class="hms-btn hms-btn-primary w-100 mt-2" :disabled="verifying || code.length !== 6" @click="verifyOtp">
              <span v-if="verifying"><span class="spinner-border spinner-border-sm me-1"></span> Verifying…</span>
              <span v-else><i class="mdi mdi-check"></i> Verify and grant consent</span>
            </button>
            <button class="hms-btn hms-btn-sm w-100 mt-2" @click="step='send'"><i class="mdi mdi-refresh"></i> Resend code</button>
          </div>

          <div v-else-if="step === 'done'" class="text-center">
            <i class="mdi mdi-check-decagram" style="font-size:48px;color:#16a34a;"></i>
            <h6 class="mt-2">Consent recorded</h6>
            <p class="hms-muted-sm">Patient has granted access to <strong>{{ categoryLabel || categoryCode }}</strong>. All future access is audited.</p>
          </div>

          <div v-if="error" class="alert" style="background:#fee2e2;color:#991b1b;padding:8px 12px;border-radius:6px;font-size:13px;margin-top:8px;">
            <i class="mdi mdi-alert-circle"></i> {{ error }}
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import { executeJson, hms } from "@/api";

export default {
  name: "ConsentOtpButton",
  props: {
    patientId:     { type: [Number, String], required: true },
    categoryCode:  { type: String, required: true },
    categoryLabel: { type: String, default: "" },
    hasConsent:    { type: Boolean, default: false }
  },
  emits: ["granted"],
  data() {
    return {
      dialog: false,
      step: "send",
      sending: false,
      verifying: false,
      code: "",
      channel: "",
      phoneMasked: "",
      error: ""
    };
  },
  methods: {
    open() {
      this.dialog = true;
      this.step = "send";
      this.code = "";
      this.error = "";
    },
    cancel() {
      this.dialog = false;
    },
    async sendOtp() {
      this.error = "";
      this.sending = true;
      try {
        const r = await executeJson({
          function: "requestConsentOtp",
          patient_id: this.patientId,
          category_code: this.categoryCode
        }, hms);
        if (r.data && r.data.success) {
          this.channel = r.data.data?.channel || "SMS + email";
          this.phoneMasked = r.data.data?.phone_masked || "";
          this.step = "verify";
        } else {
          this.error = r.data?.message || "Could not send OTP.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Network error. Try again.";
      } finally {
        this.sending = false;
      }
    },
    async verifyOtp() {
      this.error = "";
      this.verifying = true;
      try {
        const r = await executeJson({
          function: "verifyConsentOtp",
          patient_id: this.patientId,
          category_code: this.categoryCode,
          code: this.code.trim()
        }, hms);
        if (r.data && r.data.success) {
          this.step = "done";
          this.$emit("granted", { patient_id: this.patientId, category_code: this.categoryCode });
          setTimeout(() => { this.dialog = false; }, 1800);
        } else {
          this.error = r.data?.message || "Code rejected.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Network error.";
      } finally {
        this.verifying = false;
      }
    }
  }
};
</script>

<style scoped>
.hms-modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.45); display: flex; align-items: center; justify-content: center; z-index: 1080; }
.hms-modal { background: #fff; border-radius: 12px; width: 92%; max-width: 440px; box-shadow: 0 20px 60px rgba(15,23,42,.25); overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-head h5 { font-size: 16px; }
.hms-modal-close { background: none; border: none; font-size: 22px; color: #64748b; cursor: pointer; padding: 0 4px; }
.hms-modal-body { padding: 16px 18px; }
.hms-btn-outline-primary { background: #fff; border: 1px solid #2c5fd1; color: #2c5fd1; }
.hms-btn-success { background: #16a34a; color: #fff; border: 1px solid #16a34a; }
</style>
